import type { BBox } from './cameras';

const OVERPASS_URL = 'https://overpass-api.de/api/interpreter';

const MAXSPEED_DEFAULTS: Record<string, number> = {
  'BE:urban': 50,
  'BE:rural': 70,
  'BE:motorway': 120,
  'FR:urban': 50,
  'FR:rural': 80,
  'FR:motorway': 130,
  'NL:urban': 50,
  'NL:rural': 80,
  'NL:motorway': 100,
  walk: 10,
  living_street: 20,
};

export function parseMaxspeed(value: string | undefined | null): number | null {
  if (!value) return null;
  // OSM standard: bare integer is km/h
  if (/^\d+$/.test(value)) return parseInt(value, 10);
  const kph = value.match(/^(\d+)\s*(?:kph|km\/h|kmh)$/i);
  if (kph) return parseInt(kph[1], 10);
  const mph = value.match(/^(\d+)\s*mph$/i);
  if (mph) return Math.round(parseInt(mph[1], 10) * 1.60934);
  return MAXSPEED_DEFAULTS[value] ?? null;
}

/** A maxspeed-tagged road, geometry in way order, prefetched for local matching. */
export interface SpeedLimitWay {
  limitKph: number;
  points: { lat: number; lon: number }[];
}

interface OverpassGeomWay {
  geometry?: { lat: number; lon: number }[];
  tags?: Record<string, string | undefined>;
}
interface OverpassResponse {
  elements: OverpassGeomWay[];
}

// Fetches every maxspeed-tagged road in the bbox with its geometry, so the current
// limit can be matched client-side per GPS tick instead of round-tripping Overpass at
// every road change. ["highway"] keeps maxspeed-tagged rail/waterways out. Throws on
// fetch / parse error and on AbortError so callers can keep the previous set.
export async function fetchSpeedLimitWays(bbox: BBox, signal?: AbortSignal): Promise<SpeedLimitWay[]> {
  const query = `[out:json][timeout:25];way["maxspeed"]["highway"](${bbox.south},${bbox.west},${bbox.north},${bbox.east});out geom;`;
  const resp = await fetch(OVERPASS_URL + '?data=' + encodeURIComponent(query), { signal });
  if (!resp.ok) throw new Error(`Overpass returned ${resp.status}`);
  const data: OverpassResponse = await resp.json();
  const ways: SpeedLimitWay[] = [];
  for (const el of data.elements) {
    const limitKph = parseMaxspeed(el.tags?.maxspeed);
    if (limitKph === null || !el.geometry || el.geometry.length < 2) continue;
    ways.push({ limitKph, points: el.geometry.map((p) => ({ lat: p.lat, lon: p.lon })) });
  }
  return ways;
}

// How far a fix may sit from a way's centerline and still count as "on that road" —
// GPS drift plus wide carriageways.
const MAX_MATCH_M = 35;
// Segments pointing more than this away from the direction of travel are a crossing
// road, not ours. Undirected, so a way mapped against travel direction still aligns.
const HEADING_TOLERANCE_DEG = 50;

/**
 * The limit of the way nearest to (lat, lon), or null when none is within reach.
 * With a heading (degrees clockwise from north — pass null when stationary, GPS heading
 * is noise below a few km/h), ways aligned with travel beat nearer crossing ways, which
 * stops the old road's limit sticking through a junction. A misaligned way still wins
 * over no match at all, so an untagged road shows its surroundings rather than blanking
 * on every curve the tolerance misjudges.
 */
export function matchSpeedLimit(
  ways: SpeedLimitWay[],
  lat: number,
  lon: number,
  headingDegrees: number | null = null,
): number | null {
  // Equirectangular metres around the fix — exact enough at these distances.
  const mPerDegLat = 111_320;
  const mPerDegLon = mPerDegLat * Math.cos((lat * Math.PI) / 180);

  let best: { d: number; limitKph: number; aligned: boolean } | null = null;

  for (const way of ways) {
    for (let i = 1; i < way.points.length; i++) {
      const a = way.points[i - 1];
      const b = way.points[i];
      const ax = (a.lon - lon) * mPerDegLon;
      const ay = (a.lat - lat) * mPerDegLat;
      const dx = (b.lon - lon) * mPerDegLon - ax;
      const dy = (b.lat - lat) * mPerDegLat - ay;
      // Closest point on segment a→b to the fix (the origin)
      const lenSq = dx * dx + dy * dy;
      const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / lenSq));
      const d = Math.hypot(ax + t * dx, ay + t * dy);
      if (d > MAX_MATCH_M) continue;

      let aligned = true;
      if (headingDegrees != null && lenSq > 0) {
        const bearing = (Math.atan2(dx, dy) * 180) / Math.PI;
        let diff = Math.abs((((bearing - headingDegrees) % 180) + 180) % 180);
        if (diff > 90) diff = 180 - diff;
        aligned = diff <= HEADING_TOLERANCE_DEG;
      }

      // Alignment first; within the same alignment class nearest wins, exact ties
      // going to the lower (conservative) limit.
      if (
        !best ||
        (aligned && !best.aligned) ||
        (aligned === best.aligned && (d < best.d || (d === best.d && way.limitKph < best.limitKph)))
      ) {
        best = { d, limitKph: way.limitKph, aligned };
      }
    }
  }
  return best?.limitKph ?? null;
}
